// Hardcoded mock data for the family care team preview screens.
// Not connected to any backend. Numbers chosen to feel like a real family
// who has been on BrightPath for a few weeks and is actively building out
// their provider team and uploading documents.

export const MOCK_FAMILY = {
    primaryParent: 'Maria Rodriguez',
    childFirstName: 'Eli',
    childAge: '4 years',
    onboardedAt: '2026-04-13',
    lastActiveAt: '2026-05-16',
}

// --- Care team providers ---

export type ProviderType =
    | 'developmental-pediatrician'
    | 'aba-bcba'
    | 'speech-language'
    | 'occupational-therapy'
    | 'primary-care'
    | 'school-iep'
    | 'psychologist'
    | 'other'

export const PROVIDER_TYPE_LABEL: Record<ProviderType, string> = {
    'developmental-pediatrician': 'Developmental pediatrician',
    'aba-bcba': 'ABA / BCBA',
    'speech-language': 'Speech-language pathology',
    'occupational-therapy': 'Occupational therapy',
    'primary-care': 'Primary care pediatrician',
    'school-iep': 'School / IEP coordinator',
    psychologist: 'Child psychologist',
    other: 'Other',
}

export type ProviderStatus = 'connected' | 'invited' | 'manual'

export interface MockProvider {
    id: string
    type: ProviderType
    clinicianName: string
    organization: string
    email: string
    phone?: string
    status: ProviderStatus
    addedAt: string
    documentCount: number
    lastDocumentAt: string | null
    hasOpenConflict: boolean
}

export const MOCK_PROVIDERS: MockProvider[] = [
    {
        id: 'prov_001',
        type: 'developmental-pediatrician',
        clinicianName: 'Dr. Anita Desai',
        organization: 'Brevard Developmental Pediatrics',
        email: 'office@brevarddev.example',
        phone: '(321) 555-0188',
        status: 'connected',
        addedAt: '2026-04-14',
        documentCount: 2,
        lastDocumentAt: '2026-04-22',
        hasOpenConflict: false,
    },
    {
        id: 'prov_002',
        type: 'aba-bcba',
        clinicianName: 'Megan Foster, BCBA',
        organization: 'Sunshine Pediatric Therapy',
        email: 'megan@sunshinepediatric.example',
        phone: '(321) 555-0142',
        status: 'connected',
        addedAt: '2026-04-18',
        documentCount: 3,
        lastDocumentAt: '2026-05-10',
        hasOpenConflict: true,
    },
    {
        id: 'prov_003',
        type: 'speech-language',
        clinicianName: 'Jordan Lee, MS CCC-SLP',
        organization: 'Coastal Speech & Language',
        email: 'jordan.lee@coastalslp.example',
        status: 'connected',
        addedAt: '2026-04-20',
        documentCount: 2,
        lastDocumentAt: '2026-05-08',
        hasOpenConflict: true,
    },
    {
        id: 'prov_004',
        type: 'occupational-therapy',
        clinicianName: 'Priya Shah, OTR/L',
        organization: 'Sunshine Pediatric Therapy',
        email: 'priya@sunshinepediatric.example',
        status: 'connected',
        addedAt: '2026-04-25',
        documentCount: 1,
        lastDocumentAt: '2026-05-02',
        hasOpenConflict: false,
    },
    {
        id: 'prov_005',
        type: 'primary-care',
        clinicianName: 'Dr. Tomas Vega',
        organization: 'Family Care Pediatrics',
        email: 'front.desk@familycarepeds.example',
        phone: '(321) 555-0107',
        status: 'manual',
        addedAt: '2026-05-01',
        documentCount: 1,
        lastDocumentAt: '2026-05-01',
        hasOpenConflict: false,
    },
    {
        id: 'prov_006',
        type: 'school-iep',
        clinicianName: 'Karen Nguyen',
        organization: 'Mims Elementary (Brevard County Schools)',
        email: 'k.nguyen@brevardschools.example',
        status: 'invited',
        addedAt: '2026-05-12',
        documentCount: 0,
        lastDocumentAt: null,
        hasOpenConflict: false,
    },
]

// --- Documents ---

export type DocumentKind =
    | 'evaluation-report'
    | 'iep'
    | 'care-plan'
    | 'progress-note'
    | 'prescription'
    | 'assessment'
    | 'other'

export const DOC_KIND_LABEL: Record<DocumentKind, string> = {
    'evaluation-report': 'Evaluation report',
    iep: 'IEP / 504 plan',
    'care-plan': 'Care plan / treatment plan',
    'progress-note': 'Progress note',
    prescription: 'Prescription / referral',
    assessment: 'Assessment / screener',
    other: 'Other',
}

export type DocumentStatus = 'analyzed' | 'analyzing' | 'conflict' | 'pending-upload'

export interface ExtractedFinding {
    label: string
    value: string
    impactsRoadmap: boolean
}

export interface MockDocument {
    id: string
    providerId: string
    title: string
    kind: DocumentKind
    fileName: string
    pageCount: number
    uploadedAt: string
    status: DocumentStatus
    summary: string
    findings: ExtractedFinding[]
    milestoneIds: string[] // roadmap milestones this document is cited as a source for
    isOnboarding?: boolean // synthetic source representing the family's onboarding answers
}

// Synthetic source representing the family's onboarding answers, treated as a
// first-class roadmap source alongside provider documents.
export const ONBOARDING_DOC_ID = 'doc_onboarding'

export const MOCK_DOCUMENTS: MockDocument[] = [
    {
        id: ONBOARDING_DOC_ID,
        providerId: '',
        title: 'Your onboarding answers',
        kind: 'assessment',
        fileName: 'onboarding-2026-04-13',
        pageCount: 0,
        uploadedAt: '2026-04-13',
        status: 'analyzed',
        isOnboarding: true,
        summary:
            'You completed BrightPath onboarding on 2026-04-13. You shared that Eli is 4 years ' +
            'old, communicates mostly through single words and gestures, has high sensory needs ' +
            'around sounds and clothing, and that you were starting the diagnostic process. ' +
            'This formed the first version of the roadmap before any provider documents were ' +
            'uploaded.',
        findings: [
            { label: "Child's age at onboarding", value: '4 years', impactsRoadmap: true },
            { label: 'Communication level', value: 'Single words + gestures', impactsRoadmap: true },
            {
                label: 'Top support need',
                value: 'Sensory regulation (sounds, clothing)',
                impactsRoadmap: true,
            },
            {
                label: 'Diagnostic status at onboarding',
                value: 'Evaluation scheduled, not yet diagnosed',
                impactsRoadmap: true,
            },
            { label: 'School status', value: 'VPK, no IEP in place', impactsRoadmap: true },
            { label: 'Primary parent goal', value: 'Help Eli build daily living skills', impactsRoadmap: false },
        ],
        milestoneIds: ['ms_a', 'ms_c', 'ms_i', 'ms_j'],
    },
    {
        id: 'doc_001',
        providerId: 'prov_001',
        title: 'ADOS-2 evaluation summary',
        kind: 'evaluation-report',
        fileName: 'eli-ados2-2026-04-18.pdf',
        pageCount: 14,
        uploadedAt: '2026-04-22',
        status: 'analyzed',
        summary:
            'ADOS-2 Module 2 administered 2026-04-18. Comparison score 7 (high). ' +
            'Diagnosis confirmed: Autism Spectrum Disorder, Level 2. ' +
            'Recommendations: ABA 15-25 hrs/week, speech-language services 2x/week, ' +
            'occupational therapy with sensory-integration focus, dyad social skills group once stable.',
        findings: [
            { label: 'Diagnosis', value: 'ASD, Level 2 (DSM-5-TR)', impactsRoadmap: true },
            { label: 'ADOS-2 comparison score', value: '7 (high)', impactsRoadmap: false },
            { label: 'ABA recommendation', value: '15-25 hrs/week', impactsRoadmap: true },
            { label: 'Speech recommendation', value: '2x/week, functional communication focus', impactsRoadmap: true },
            { label: 'OT recommendation', value: 'Sensory-integration focus, 1x/week', impactsRoadmap: true },
            { label: 'Re-evaluation cadence', value: 'Annual', impactsRoadmap: false },
        ],
        milestoneIds: ['ms_a', 'ms_c', 'ms_d', 'ms_e'],
    },
    {
        id: 'doc_002',
        providerId: 'prov_001',
        title: 'Genetic testing referral letter',
        kind: 'prescription',
        fileName: 'genetic-referral-2026-04-22.pdf',
        pageCount: 1,
        uploadedAt: '2026-04-22',
        status: 'analyzed',
        summary:
            'Referral to Nemours Genetics for chromosomal microarray + Fragile X testing. ' +
            'Routine, not urgent. Insurance pre-auth recommended.',
        findings: [
            { label: 'Test ordered', value: 'Chromosomal microarray + Fragile X', impactsRoadmap: true },
            { label: 'Urgency', value: 'Routine', impactsRoadmap: false },
            { label: 'Pre-auth required', value: 'Yes (insurance)', impactsRoadmap: false },
        ],
        milestoneIds: ['ms_b'],
    },
    {
        id: 'doc_003',
        providerId: 'prov_002',
        title: 'ABA initial treatment plan',
        kind: 'care-plan',
        fileName: 'aba-treatment-plan-april.pdf',
        pageCount: 22,
        uploadedAt: '2026-04-25',
        status: 'analyzed',
        summary:
            '20 hrs/week of direct ABA recommended (within the 15-25 hr range from the dev pediatrician). ' +
            'Six initial program targets: requesting (manding), tolerating denial, joint attention, ' +
            'functional play, transition warnings, and self-care (handwashing). Parent training 1 hr/week.',
        findings: [
            { label: 'Hours / week', value: '20 hrs direct + 1 hr parent training', impactsRoadmap: true },
            { label: 'Primary programs', value: 'Manding, tolerating no, joint attention, play, transitions, handwashing', impactsRoadmap: true },
            { label: 'Reinforcement system', value: 'Token economy (visual board)', impactsRoadmap: true },
            { label: 'Re-assessment', value: 'Every 6 months (next: October 2026)', impactsRoadmap: false },
        ],
        milestoneIds: ['ms_d', 'ms_g', 'ms_h'],
    },
    {
        id: 'doc_004',
        providerId: 'prov_002',
        title: 'ABA April progress note',
        kind: 'progress-note',
        fileName: 'aba-progress-2026-04-30.pdf',
        pageCount: 3,
        uploadedAt: '2026-05-02',
        status: 'analyzed',
        summary:
            'Manding mastered for 5 preferred items. Token economy effective. ' +
            'Recommends adding sensory diet collaboration with OT and a visual schedule at home. ' +
            'Notes parent friction at bedtime.',
        findings: [
            { label: 'Mastered targets', value: 'Manding for 5 items, "all done" signal', impactsRoadmap: true },
            { label: 'Open recommendation', value: 'Cross-discipline sensory diet w/ OT', impactsRoadmap: true },
            { label: 'Home strategy', value: 'Visual schedule for morning + bedtime', impactsRoadmap: true },
        ],
        milestoneIds: ['ms_h'],
    },
    {
        id: 'doc_005',
        providerId: 'prov_002',
        title: 'ABA hours change recommendation',
        kind: 'progress-note',
        fileName: 'aba-hours-revision-2026-05-10.pdf',
        pageCount: 2,
        uploadedAt: '2026-05-10',
        status: 'conflict',
        summary:
            'Megan Foster, BCBA, recommends stepping ABA hours up from 20 to 25 hrs/week to ' +
            'accelerate joint-attention and play targets ahead of summer. This raises a conflict ' +
            'with the SLP recommendation (Jordan Lee) that ABA hours stay at 20 to leave bandwidth ' +
            'for a planned speech-intensive block in June.',
        findings: [
            { label: 'New recommendation', value: 'ABA 25 hrs/week starting 2026-06-01', impactsRoadmap: true },
            {
                label: 'Rationale',
                value: 'Accelerate joint attention + functional play before kindergarten',
                impactsRoadmap: false,
            },
            { label: 'Conflicts with', value: 'SLP June intensive plan (doc_006)', impactsRoadmap: true },
        ],
        milestoneIds: ['ms_d'],
    },
    {
        id: 'doc_006',
        providerId: 'prov_003',
        title: 'Speech evaluation',
        kind: 'evaluation-report',
        fileName: 'slp-eval-2026-04-26.pdf',
        pageCount: 9,
        uploadedAt: '2026-04-28',
        status: 'analyzed',
        summary:
            'Receptive language at 2;6 age-equivalent. Expressive language at 1;10. ' +
            'Three annual goals: increase MLU to 3+, master 25 functional core-vocabulary signs, ' +
            'and use a 2-icon AAC request with 80% accuracy. Recommends 2x/week direct and a ' +
            'summer intensive in June (5x/week for 3 weeks).',
        findings: [
            { label: 'Receptive language', value: '2;6 age-equivalent', impactsRoadmap: false },
            { label: 'Expressive language', value: '1;10 age-equivalent', impactsRoadmap: false },
            {
                label: 'Annual goals',
                value: 'MLU 3+, 25 core vocab signs, 2-icon AAC at 80%',
                impactsRoadmap: true,
            },
            {
                label: 'Recommended cadence',
                value: '2x/week + June intensive (5x/wk for 3 wks)',
                impactsRoadmap: true,
            },
            {
                label: 'AAC trial',
                value: 'Two apps to evaluate (Proloquo2Go, TouchChat)',
                impactsRoadmap: true,
            },
        ],
        milestoneIds: ['ms_e', 'ms_f'],
    },
    {
        id: 'doc_007',
        providerId: 'prov_003',
        title: 'Speech April progress note',
        kind: 'progress-note',
        fileName: 'slp-progress-2026-05-08.pdf',
        pageCount: 2,
        uploadedAt: '2026-05-08',
        status: 'conflict',
        summary:
            'Recommends ABA team hold hours at 20/week through June so that the speech June ' +
            'intensive can run without competing with the ABA schedule. This conflicts with ' +
            "Megan Foster's recent recommendation to step ABA hours up to 25.",
        findings: [
            { label: 'Position', value: 'Hold ABA at 20 hrs/wk through June', impactsRoadmap: true },
            { label: 'Reason', value: 'Speech June intensive 5x/wk for 3 wks', impactsRoadmap: false },
        ],
        milestoneIds: ['ms_d', 'ms_f'],
    },
    {
        id: 'doc_008',
        providerId: 'prov_004',
        title: 'OT sensory profile + treatment plan',
        kind: 'care-plan',
        fileName: 'ot-sensory-profile-2026-05-02.pdf',
        pageCount: 11,
        uploadedAt: '2026-05-02',
        status: 'analyzed',
        summary:
            'Sensory profile shows high sensory-seeking for proprioceptive input, high avoidance ' +
            'of auditory + tactile (clothing tags). Daily sensory-diet recommended: heavy-work ' +
            'activity before transitions, 10-min sensory break before bedtime, deep-pressure ' +
            'brushing protocol nightly. Aligns with ABA recommendation for visual schedule.',
        findings: [
            {
                label: 'Sensory-seeking',
                value: 'High: proprioceptive (jumping, squeezing)',
                impactsRoadmap: true,
            },
            {
                label: 'Sensory-avoiding',
                value: 'High: auditory, tactile (tags, seams)',
                impactsRoadmap: true,
            },
            {
                label: 'Bedtime protocol',
                value: '10-min sensory break + brushing',
                impactsRoadmap: true,
            },
            {
                label: 'Cross-discipline alignment',
                value: 'Coordinates with ABA visual-schedule plan',
                impactsRoadmap: false,
            },
        ],
        milestoneIds: ['ms_h'],
    },
    {
        id: 'doc_009',
        providerId: 'prov_005',
        title: 'Well-child visit summary (4yr)',
        kind: 'progress-note',
        fileName: 'wellchild-2026-05-01.pdf',
        pageCount: 4,
        uploadedAt: '2026-05-01',
        status: 'analyzed',
        summary:
            '4-year well-child visit. Growth on track. Vaccinations up to date. Mild constipation ' +
            'noted (common with selective eating in autism). Pediatrician aware of ASD diagnosis ' +
            'and concurrent ABA + SLP + OT services. Suggests a feeding therapy consult if ' +
            'selective eating worsens.',
        findings: [
            { label: 'Growth', value: '50th percentile both height + weight', impactsRoadmap: false },
            { label: 'Vaccinations', value: 'Up to date', impactsRoadmap: false },
            { label: 'Open concern', value: 'Mild constipation, selective eating', impactsRoadmap: true },
            { label: 'Suggested next step', value: 'Feeding therapy if selective eating worsens', impactsRoadmap: true },
        ],
        milestoneIds: [],
    },
]

// --- Conflicts ---

export type ConflictStatus = 'open' | 'clarifying' | 'resolved'

export interface ConflictMessage {
    role: 'ai' | 'family'
    text: string
    at: string
}

export interface MockConflict {
    id: string
    status: ConflictStatus
    title: string
    summary: string
    detectedAt: string
    providerAId: string
    providerADocId: string
    providerAPosition: string
    providerBId: string
    providerBDocId: string
    providerBPosition: string
    impactedMilestoneIds: string[]
    thread: ConflictMessage[]
}

export const MOCK_CONFLICTS: MockConflict[] = [
    {
        id: 'conf_001',
        status: 'clarifying',
        title: 'ABA hours: 25 vs 20 through June',
        summary:
            'Your BCBA recommends increasing ABA from 20 to 25 hrs/week starting June 1. ' +
            'Your SLP recommends holding ABA at 20 hrs so a planned June speech intensive ' +
            '(5x/week for 3 weeks) can run without overlap. BrightPath needs your input to ' +
            'update the roadmap.',
        detectedAt: '2026-05-10',
        providerAId: 'prov_002',
        providerADocId: 'doc_005',
        providerAPosition:
            'ABA at 25 hrs/week starting 2026-06-01 to accelerate joint attention and play before kindergarten.',
        providerBId: 'prov_003',
        providerBDocId: 'doc_007',
        providerBPosition:
            'Hold ABA at 20 hrs/week through June so the speech intensive (5x/week for 3 weeks) does not compete.',
        impactedMilestoneIds: ['ms_d', 'ms_f'],
        thread: [
            {
                role: 'ai',
                text:
                    'I noticed your BCBA and your SLP have different recommendations for ABA ' +
                    'hours in June. The BCBA wants to step up to 25 hrs/wk; the SLP wants to ' +
                    'hold at 20 hrs/wk so the June speech intensive can run. A few quick ' +
                    'questions will help me update the roadmap:',
                at: '2026-05-10 4:12 PM',
            },
            {
                role: 'ai',
                text:
                    '1) Is the June speech intensive still planned, or has it been postponed? ' +
                    '2) If both providers had to choose, do you have a preference between ' +
                    'accelerating ABA or running the speech intensive? 3) Would you like me to ' +
                    'draft a question to send back to both providers so they can decide together?',
                at: '2026-05-10 4:12 PM',
            },
            {
                role: 'family',
                text:
                    'The speech intensive is still planned. I would love for both providers ' +
                    'to weigh in together if you can help draft something.',
                at: '2026-05-12 8:41 AM',
            },
            {
                role: 'ai',
                text:
                    'Got it. I drafted a message that you can review and send to Megan Foster ' +
                    "(BCBA) and Jordan Lee (SLP). Once they agree, I'll update Eli's roadmap " +
                    'automatically and show you what changed. Want to see the draft?',
                at: '2026-05-12 8:43 AM',
            },
        ],
    },
    {
        id: 'conf_002',
        status: 'resolved',
        title: 'Sensory-diet bedtime: ABA vs OT',
        summary:
            'Earlier in May, the ABA plan recommended a visual-schedule bedtime routine and ' +
            'the OT plan recommended a sensory break + brushing protocol. You confirmed both ' +
            'can run side-by-side. BrightPath merged them into a single bedtime sequence.',
        detectedAt: '2026-05-03',
        providerAId: 'prov_002',
        providerADocId: 'doc_004',
        providerAPosition: 'Visual schedule for bedtime, posted in Eli\'s room.',
        providerBId: 'prov_004',
        providerBDocId: 'doc_008',
        providerBPosition: '10-minute sensory break + deep-pressure brushing nightly before lights-out.',
        impactedMilestoneIds: ['ms_h'],
        thread: [
            {
                role: 'ai',
                text:
                    "These don't actually conflict, they layer. Should I combine them into " +
                    'one bedtime sequence on the roadmap?',
                at: '2026-05-03 10:02 AM',
            },
            {
                role: 'family',
                text: 'Yes please.',
                at: '2026-05-03 10:04 AM',
            },
            {
                role: 'ai',
                text:
                    'Done. Bedtime milestone now reads "Sensory break + visual schedule + ' +
                    'brushing." Both providers cited as sources.',
                at: '2026-05-03 10:04 AM',
            },
        ],
    },
]

// --- Roadmap with provider citations ---

export type MilestoneChangeKind = 'unchanged' | 'new' | 'modified' | 'archived'

export interface MockRoadmapTask {
    id: string
    label: string
    description?: string
    isChecked: boolean
}

export type RoadmapPhase = 'current-focus' | 'upcoming-focus' | 'looking-ahead'

export interface RoadmapPhaseDef {
    id: RoadmapPhase
    label: string
    horizon: string
    blurb: string
}

export const ROADMAP_PHASES: RoadmapPhaseDef[] = [
    {
        id: 'current-focus',
        label: 'In Current Focus',
        horizon: 'Next 4-6 weeks',
        blurb: 'What your care team is working on right now.',
    },
    {
        id: 'upcoming-focus',
        label: 'Upcoming Focus',
        horizon: 'Next 2-3 months',
        blurb: 'Coming up soon. Begin preparing.',
    },
    {
        id: 'looking-ahead',
        label: 'Looking Ahead',
        horizon: '3+ months out',
        blurb: 'On the horizon. Worth being aware of.',
    },
]

export interface MockRoadmapMilestone {
    id: string
    title: string
    description: string
    sequence: string
    phase: RoadmapPhase
    status: 'not-started' | 'in-progress' | 'completed'
    changeKind: MilestoneChangeKind
    previousTitle?: string
    previousDescription?: string
    archivedReason?: string
    sourceDocIds: string[]
    sourceNote?: string
    timeframe: string
    tasks: MockRoadmapTask[]
}

export const MOCK_ROADMAP: MockRoadmapMilestone[] = [
    {
        id: 'ms_a',
        title: 'Confirm autism diagnosis with developmental pediatrician',
        description: 'ADOS-2 administered 2026-04-18, diagnosis confirmed.',
        sequence: 'Diagnosis & Assessment',
        phase: 'current-focus',
        status: 'completed',
        changeKind: 'unchanged',
        sourceDocIds: [ONBOARDING_DOC_ID, 'doc_001'],
        timeframe: '2-4 weeks',
        tasks: [
            { id: 'ms_a_t1', label: 'Get a referral from primary care', isChecked: true },
            { id: 'ms_a_t2', label: 'Schedule the evaluation appointment', isChecked: true },
            {
                id: 'ms_a_t3',
                label: 'Attend the evaluation and pick up the written report',
                isChecked: true,
            },
        ],
    },
    {
        id: 'ms_b',
        title: 'Complete genetic testing (chromosomal microarray + Fragile X)',
        description: 'Routine workup. Insurance pre-auth recommended. Lab: Nemours Genetics.',
        sequence: 'Diagnosis & Assessment',
        phase: 'upcoming-focus',
        status: 'in-progress',
        changeKind: 'new',
        sourceDocIds: ['doc_002'],
        sourceNote: 'Added after the dev pediatrician uploaded the referral letter.',
        timeframe: '4-6 weeks',
        tasks: [
            { id: 'ms_b_t1', label: 'Submit insurance pre-authorization', isChecked: true },
            { id: 'ms_b_t2', label: 'Schedule blood draw at Nemours Genetics', isChecked: false },
            { id: 'ms_b_t3', label: 'Review results with developmental pediatrician', isChecked: false },
        ],
    },
    {
        id: 'ms_c',
        title: 'Start ABA therapy (15-25 hrs/week)',
        description: 'BCBA-led, focus on functional communication and play.',
        sequence: 'Therapy & Intervention',
        phase: 'current-focus',
        status: 'completed',
        changeKind: 'unchanged',
        sourceDocIds: [ONBOARDING_DOC_ID, 'doc_001'],
        timeframe: '2-3 weeks',
        tasks: [
            { id: 'ms_c_t1', label: 'Identify an in-network BCBA provider', isChecked: true },
            { id: 'ms_c_t2', label: 'Complete intake assessment', isChecked: true },
            { id: 'ms_c_t3', label: 'Sign treatment plan and start first sessions', isChecked: true },
        ],
    },
    {
        id: 'ms_d',
        title: 'ABA hours: 20/week through May, decision pending for June',
        description:
            '20 hrs/week direct + 1 hr parent training. June hours are pending a conflict ' +
            'between the BCBA (25 hrs) and SLP (hold at 20 hrs).',
        sequence: 'Therapy & Intervention',
        phase: 'current-focus',
        status: 'in-progress',
        changeKind: 'modified',
        previousTitle: 'ABA 20 hrs/week',
        previousDescription: '20 hrs/week of direct ABA + 1 hr parent training.',
        sourceDocIds: ['doc_003', 'doc_005', 'doc_007'],
        sourceNote:
            'Updated after a BCBA hours change recommendation and an SLP counter-recommendation.',
        timeframe: 'Ongoing',
        tasks: [
            { id: 'ms_d_t1', label: 'Attend weekly parent training session', isChecked: true },
            { id: 'ms_d_t2', label: 'Track behavior log entries between sessions', isChecked: false },
            { id: 'ms_d_t3', label: 'Confirm June schedule once conflict is resolved', isChecked: false },
        ],
    },
    {
        id: 'ms_e',
        title: 'Speech-language therapy 2x/week',
        description: 'Annual goals: MLU 3+, 25 core-vocab signs, 2-icon AAC requesting at 80% accuracy.',
        sequence: 'Therapy & Intervention',
        phase: 'current-focus',
        status: 'in-progress',
        changeKind: 'modified',
        previousTitle: 'Speech-language therapy 1x/week',
        previousDescription: 'Initial recommendation from primary care: 1x/week.',
        sourceDocIds: ['doc_001', 'doc_006'],
        sourceNote: 'Cadence updated from 1x/week to 2x/week after speech evaluation upload.',
        timeframe: 'Ongoing',
        tasks: [
            { id: 'ms_e_t1', label: 'Practice 5 core vocab signs daily', isChecked: true },
            { id: 'ms_e_t2', label: 'Trial AAC apps at home (Proloquo2Go, TouchChat)', isChecked: false },
            { id: 'ms_e_t3', label: 'Bring AAC trial notes to next session', isChecked: false },
        ],
    },
    {
        id: 'ms_f',
        title: 'Plan: June speech-language intensive (5x/week for 3 weeks)',
        description: 'Pending a coordinated answer with the BCBA on ABA hours during this window.',
        sequence: 'Therapy & Intervention',
        phase: 'upcoming-focus',
        status: 'not-started',
        changeKind: 'new',
        sourceDocIds: ['doc_006'],
        sourceNote: 'Added after speech evaluation.',
        timeframe: 'June 2026',
        tasks: [
            { id: 'ms_f_t1', label: 'Confirm BCBA + SLP scheduling agreement', isChecked: false },
            { id: 'ms_f_t2', label: 'Block out daily 60-min sessions on the family calendar', isChecked: false },
            { id: 'ms_f_t3', label: 'Arrange transportation logistics', isChecked: false },
        ],
    },
    {
        id: 'ms_g',
        title: 'Find an occupational therapist near you',
        description: 'Connected to Priya Shah, OTR/L at Sunshine Pediatric Therapy.',
        sequence: 'Therapy & Intervention',
        phase: 'current-focus',
        status: 'completed',
        changeKind: 'archived',
        archivedReason: 'OT provider added to your team. This step is no longer needed.',
        sourceDocIds: ['doc_001'],
        timeframe: '2-3 weeks',
        tasks: [],
    },
    {
        id: 'ms_h',
        title: 'Bedtime: sensory break + visual schedule + brushing protocol',
        description:
            'Merged bedtime routine combining ABA visual schedule and OT sensory + brushing. ' +
            '10-min sensory break, follow visual schedule, brushing before lights-out.',
        sequence: 'Behavior & Daily Living',
        phase: 'current-focus',
        status: 'in-progress',
        changeKind: 'new',
        sourceDocIds: ['doc_004', 'doc_008'],
        sourceNote:
            'Created by merging the ABA visual-schedule recommendation with the OT sensory-diet ' +
            'plan, after confirming with you that they could run together.',
        timeframe: 'Daily',
        tasks: [
            { id: 'ms_h_t1', label: 'Print the visual schedule and hang in bedroom', isChecked: true },
            { id: 'ms_h_t2', label: 'Run 10-min sensory break each night', isChecked: true },
            { id: 'ms_h_t3', label: 'Follow brushing protocol nightly', isChecked: false },
            { id: 'ms_h_t4', label: 'Log how Eli responded for one week', isChecked: false },
        ],
    },
    {
        id: 'ms_i',
        title: 'Request IEP evaluation from school district',
        description: 'Send a formal written request, certified mail.',
        sequence: 'School & Community',
        phase: 'upcoming-focus',
        status: 'not-started',
        changeKind: 'unchanged',
        sourceDocIds: [ONBOARDING_DOC_ID],
        timeframe: '1-2 weeks',
        tasks: [
            { id: 'ms_i_t1', label: 'Draft the written request letter', isChecked: false },
            { id: 'ms_i_t2', label: 'Send via certified mail with return receipt', isChecked: false },
            { id: 'ms_i_t3', label: 'Follow up if no response after 15 school days', isChecked: false },
        ],
    },
    {
        id: 'ms_j',
        title: 'Connect with a local parent support group',
        description: 'Find a peer group that meets monthly.',
        sequence: 'School & Community',
        phase: 'looking-ahead',
        status: 'not-started',
        changeKind: 'unchanged',
        sourceDocIds: [ONBOARDING_DOC_ID],
        timeframe: 'Ongoing',
        tasks: [
            { id: 'ms_j_t1', label: 'Search for local autism parent meetups', isChecked: false },
            { id: 'ms_j_t2', label: 'Attend a first meeting', isChecked: false },
        ],
    },
    {
        id: 'ms_k',
        title: 'Kindergarten transition planning',
        description:
            'Coordinate with school IEP team to plan a smooth transition into kindergarten, ' +
            'including placement decisions and supports needed in the new environment.',
        sequence: 'School & Community',
        phase: 'looking-ahead',
        status: 'not-started',
        changeKind: 'unchanged',
        sourceDocIds: [ONBOARDING_DOC_ID],
        timeframe: 'Fall 2026',
        tasks: [
            {
                id: 'ms_k_t1',
                label: 'Tour potential elementary schools (public + autism-friendly private)',
                isChecked: false,
            },
            {
                id: 'ms_k_t2',
                label: 'Request transition meeting with current preschool and receiving school',
                isChecked: false,
            },
            { id: 'ms_k_t3', label: 'Update IEP to reflect kindergarten goals', isChecked: false },
        ],
    },
    {
        id: 'ms_l',
        title: 'Annual ABA re-assessment (October 2026)',
        description:
            'Six-month and twelve-month progress review against the initial ABA treatment plan goals.',
        sequence: 'Therapy & Intervention',
        phase: 'looking-ahead',
        status: 'not-started',
        changeKind: 'unchanged',
        sourceDocIds: ['doc_003'],
        timeframe: 'October 2026',
        tasks: [
            { id: 'ms_l_t1', label: 'Confirm appointment with Megan Foster, BCBA', isChecked: false },
            { id: 'ms_l_t2', label: 'Compile parent observations from the prior 6 months', isChecked: false },
        ],
    },
]

export const ROADMAP_SEQUENCES = [
    'Diagnosis & Assessment',
    'Therapy & Intervention',
    'Behavior & Daily Living',
    'School & Community',
]

// --- AI activity feed (across the whole care team) ---

export interface MockAiInsight {
    id: string
    at: string
    headline: string
    detail: string
    kind: 'new-info' | 'roadmap-update' | 'conflict' | 'cross-discipline'
    linkPath?: string
    linkLabel?: string
}

export const MOCK_AI_INSIGHTS: MockAiInsight[] = [
    {
        id: 'ins_001',
        at: '2 hours ago',
        headline: 'ABA hours conflict needs your input',
        detail:
            'Megan Foster (BCBA) wants 25 hrs/wk in June. Jordan Lee (SLP) wants to hold at ' +
            '20 hrs/wk. The roadmap is paused on this question.',
        kind: 'conflict',
        linkPath: '/admin/family-care-team-preview/conflicts/conf_001',
        linkLabel: 'Open conflict',
    },
    {
        id: 'ins_002',
        at: 'Yesterday',
        headline: 'OT sensory diet merged into bedtime milestone',
        detail:
            "Priya Shah's sensory profile recommends a brushing protocol. I combined it with " +
            'the ABA visual-schedule plan into one bedtime milestone (you confirmed last week).',
        kind: 'cross-discipline',
        linkPath: '/admin/family-care-team-preview/roadmap',
        linkLabel: 'See roadmap update',
    },
    {
        id: 'ins_003',
        at: '4 days ago',
        headline: 'Speech evaluation raised cadence from 1x to 2x per week',
        detail: 'Jordan Lee\'s evaluation supports 2x/week. Eli\'s roadmap now reflects this.',
        kind: 'roadmap-update',
        linkPath: '/admin/family-care-team-preview/roadmap',
        linkLabel: 'See roadmap update',
    },
    {
        id: 'ins_004',
        at: 'Last week',
        headline: 'Genetic testing milestone added',
        detail:
            'Dr. Desai uploaded a referral letter for chromosomal microarray + Fragile X. ' +
            'Added to the Diagnosis & Assessment sequence.',
        kind: 'new-info',
    },
]

// --- Team completeness signals (for the My Care Team aggregate) ---

export const MOCK_TEAM_SIGNALS = {
    providersOnTeam: MOCK_PROVIDERS.length,
    documentsUploaded: MOCK_DOCUMENTS.length,
    openConflicts: MOCK_CONFLICTS.filter((c) => c.status !== 'resolved').length,
    roadmapMilestones: MOCK_ROADMAP.filter((m) => m.changeKind !== 'archived').length,
    completenessPercent: 78, // hand-tuned for the demo
    completenessReasons: [
        'IEP coordinator invited but has not uploaded the current IEP yet.',
        'No feeding-therapy provider on file; primary care flagged selective eating as a watch item.',
    ],
}
